
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc.js';
import timezone from 'dayjs/plugin/timezone.js';
import lodash from 'lodash';

dayjs.extend(utc);
dayjs.extend(timezone);

export const MAX_THREADS = 7;

export const NO_TITLE = 'Untitled';

export const PUNCTUATION_REGEX = /(?<=\w[.?!])\s/g;

export async function timeOperation(operation) {
  const startTime = performance.now();
  const returnValue = await operation();
  return [performance.now() - startTime, returnValue];
}

export function now() {
  return dayjs().toISOString();
}

export function today() {
  return dayjs().tz('America/Denver').format('YYYY-MM-DD');
}

export const COLOR = {
  reset: "\x1b[0m",
  red: "\x1b[31m",
  green: "\x1b[32m",
  yellow: "\x1b[33m",
  blue: "\x1b[34m",
  magenta: "\x1b[35m",
  cyan: "\x1b[36m",
};

export function escapeXml(unsafe) {
  return unsafe
      .replaceAll(/&/g, '&amp;')
      .replaceAll(/</g, '&lt;')
      .replaceAll(/>/g, '&gt;')
      .replaceAll(/'/g, '&apos;')
      .replaceAll(/"/g, '&quot;');
}

// Operates on a piece of XML with unsafe characters embedded, but also some legitimate XML tags.
export function escapeTextInXml(unsafe) {
  let i = 0;
  let output = '';
  while (i < unsafe.length) {
    const openTag = unsafe.slice(i).search(/<[\\\w/]/);
    if (openTag < 0) {
      // No more tags, escape the remainder of the string
      output += escapeXml(unsafe.slice(i));
      i = unsafe.length;
    } else {
      const closeTag = unsafe.slice(i + openTag).search(/>/);
      if (closeTag < 0) {
        throw Error(`Unmatched open tag at index ${i + openTag}`);
      }
      output += escapeXml(unsafe.slice(i, i + openTag)) + unsafe.slice(i + openTag, i + openTag + closeTag + 1);
      i += openTag + closeTag + 1;
    }
  }
  return output;
}

export function countTextCharactersInXml(xml) {
  let i = 0;
  let count = 0;
  while (i < xml.length) {
    const openTag = xml.slice(i).search(/<[\\\w/]/);
    if (openTag < 0) {
      // No more tags, count the remainder of the string
      count += xml.slice(i).length;
      i = xml.length;
    } else {
      const closeTag = xml.slice(i + openTag).search(/>/);
      if (closeTag < 0) {
        throw Error(`Unmatched open tag at index ${i + openTag}`);
      }
      // Count the text before the next open tag
      count += openTag;
      i += openTag + closeTag + 1;
    }
  }
  return count;
}

// Returns a list of pairs of adjacent items in the array
export function pairs(arr) {
  return arr.slice(1).map((item, i) => [arr[i], item]);
}

// Split a large block of text into multiple parts, to limit the characters contained in each part.
export function splitTextMaxChars(text, charactersPerChunk) {
  // Short circuit, if text is too short to split
  if (text.length < charactersPerChunk) return [text];

  let parts = [ text ];

  // First split by newlines, then by sentence-ending punctuation, then by any whitespace (if
  // necessary).
  for (let splitter of [/\n+/g, PUNCTUATION_REGEX, /\s+/g]) {
    parts = parts.flatMap(p =>
        p.length < charactersPerChunk ? [p] : p.split(splitter));
  }
  if (parts.some(p => p.length > charactersPerChunk)) {
    throw Error(`Failed to split text into chunks (size=${charactersPerChunk})! ${JSON.stringify(parts.filter(p => p.length > charactersPerChunk))}`);
  }

  // Then aggregate parts back together into chunks, up to the charactersPerChunk limit.
  const newChunks = [];
  while (parts.length) {
    let chunkSize = 0;
    // Determine how many parts to merge together by incrementing partIndex
    let partIndex = 0;
    while (partIndex < parts.length && parts[partIndex].length + chunkSize + partIndex < charactersPerChunk) {
      chunkSize += parts[partIndex++].length;
    }
    const newChunk = parts.slice(0, partIndex).join('\n');
    newChunks.push(newChunk);
    parts = parts.slice(partIndex);
  }

  return newChunks;
}

export function sleep(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

// Returns true if the two sets of strings contain the same values
export function containsSameContents(a, b) {
  const aSet = new Set(a);
  const bSet = new Set(b);
  return aSet.size === bSet.size && a.every(value => bSet.has(value));
}

export function objectMask(obj, mask) {
  if (!obj) return {};

  const getValue = (path) => path.split('.').reduce((acc, key) => acc?.[key], obj);
  const setValue = (target, path, value) => {
    const parts = path.split('.');
    if (parts.length === 0) throw Error(`Empty path: ${path}`);

    let current = target;
    while (parts.length > 1) {
      const nextPart = parts.shift();
      current[nextPart] = current[nextPart] || {};
      current = current[nextPart];
    }
    current[parts[0]] = value;
  };

  const result = {};
  for (let maskElement of mask) {
    const value = getValue(maskElement);
    if (value !== undefined) {
      setValue(result, maskElement, value);
    }
  }
  return result;
}

export async function asyncGeneratorFlatMap(generator, func) {
  const output = [];

  let streamResult;
  do {
    streamResult = await generator.next();
    output.push(...(await func(streamResult.value) ?? []));
  } while (!streamResult.done);

  return output;
}

export function pause(millis) {
  return new Promise((resolve) => setTimeout(resolve, millis));
}

export function dateTimeFormatForUnit(timeUnit) {
  switch(timeUnit) {
    case 'minute':
      return  'YYYY-MM-DD HH:mm';
    case 'hour':
      return 'YYYY-MM-DD HH';
    case 'day':
      return 'YYYY-MM-DD';
    case 'month':
      return 'YYYY-MM';
    default:
      throw Error(`Unexpected timeUnit: ${timeUnit}`);
  }
}

export function unique(array) {
  return Array.from(new Set(array));
}

export function escapeRegex(s) {
  if (!s) throw Error(`Empty input: ${s}`);
  return s.replace(/[/\-\\^$*+?.()|[\]{}]/g, '\\$&');
}

export function formatDuration(durationSeconds) {
  if (!durationSeconds || durationSeconds <= 0) return '0:00:00';

  const hours = Math.floor(durationSeconds / 3600);
  const minutes = Math.floor((durationSeconds % 3600) / 60);
  const seconds = Math.floor(durationSeconds % 60);

  return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}

export function truncateText(text, maxLength) {
  if (text?.length > maxLength) return text.substring(0, maxLength - 3) + '...';
  return text;
}

export function isFreeTrial(subscription) {
  return subscription?.status === 'trialing';
}

export function getDefaultVoiceSet(organization, subscription) {
  return isFreeTrial(subscription) ? 'cheap' : 'default';
}

export function getChanges(newData, oldData) {
  const changes = {};
  for (let [key, value] of Object.entries(newData)) {
    if (typeof value !== 'object' || Array.isArray(value)) {
      // For arrays and scalars, use isEqual to check equality
      if (!lodash.isEqual(value, oldData?.[key])) {
        changes[key] = value;
      }
    } else if (value === null) {
      if (oldData?.[key] !== null) {
        changes[key] = value;
      }
    } else {
      // For objects, include only changed values
      if (!lodash.isEqual(value, oldData?.[key])) {
        changes[key] = value;
      }
    }
  }

  //console.log({ newData, oldData, changes });

  return Object.entries(changes).length > 0 ? changes : undefined;
}

// Paginates an array into chunks of a specified maximum length
export function paginate(array, maxLength) {
  if (maxLength <= 0) {
    throw Error(`maxLength must be positive: ${maxLength}`);
  }

  const pages = [];
  for (let i = 0; i < array.length; i += maxLength) {
    pages.push(array.slice(i, i + maxLength));
  }
  return pages;
}

/**
 * Generic pagination helper for APIs that follow the has_more / starting_after pattern.
 *
 * @param {Function} getFunction - A function that fetches one page of results.
 *   - On first call, receives undefined (or no argument)
 *   - On subsequent calls, receives the ID of the last item from the previous page
 *   - Should use this ID as the `starting_after` parameter in the API request
 *   - Must return an object with:
 *     - data: array of items
 *     - has_more: boolean indicating if more pages exist
 *
 * @returns {Promise<Array>} All items from all pages
 *
 * @example
 * const allPrices = await listAll((lastItemId) =>
 *   stripe.prices.list({ starting_after: lastItemId, active: true }));
 */
export async function listAll(getFunction) {
  let response;
  const items = [];
  do {
    // Pass the ID of the last item from the previous page (or undefined on first call)
    const lastItemId = response && response.data[response.data.length - 1].id;
    response = await getFunction(lastItemId);
    items.push(...response.data);
  } while (response.has_more);
  return items;
}

export function wordCount(text) {
  return text.split(/[^\w]+/).filter(Boolean).length;
}

export function audioPartExtension(audioPart) {
  return audioPart.audioFormat === 'wav' ? '.wav' : '.mp3';
}

export function generateIntroText(metadata) {
  const name = metadata?.title
      || metadata?.subject;
  const creator = metadata?.author
      || metadata?.siteName
      || metadata?.publisher;
  if (name && creator) {
    return `${name} by ${creator}`;
  } else if (name) {
    return name;
  } else if (creator) {
    return `by ${creator}`;
  } else {
    return 'content of unknown provenance';
  }
}
